import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import styles from '../styles/automationNew.module.scss';
import StatusPill from './StatusPill';

const SECRET_MASK = '**************';
const GENERIC_SECRET_TASK = 'This step uses a secret value';
const OMIT_REASONING_COPY = 'Details hidden — a secret value could not be safely masked here.';
const SECRET_MASK_NOTE = `Secret values show as ${SECRET_MASK}.`;

function timelineDotClass(status) {
  if (status === 'completed') return `${styles.timelineDot} ${styles.timelineDotCompleted}`;
  if (status === 'running') return `${styles.timelineDot} ${styles.timelineDotRunning}`;
  if (status === 'error') return `${styles.timelineDot} ${styles.timelineDotError}`;
  return styles.timelineDot;
}

/** Agentic diary filters — Story is the default for website testers. */
const FILTERS = [
  { id: 'story', label: 'Story' },
  { id: 'actions', label: 'Actions' },
  { id: 'problems', label: 'Problems' },
];

/** Tester-facing labels — not engineer telemetry. */
const CATEGORY_LABEL = {
  thought: 'Thinking',
  directive: 'Planned',
  action: 'Did',
  evaluation: 'Checked',
  recover: 'Retried',
  progress: 'Progress',
  screenshot: 'Screenshot',
  guardrail: 'Blocked',
  validation: 'Review',
  system: 'System',
};

const REASON_KEYS = ['What', 'Happened', 'Why', 'Next'];

/** Parse Fast Fail What/Happened/Why/Next (newlines or legacy " | " joins). */
function parseFourPartReason(text) {
  if (!text || typeof text !== 'string') return null;
  const normalized = String(text).replace(/\s*\|\s*(?=(?:What|Happened|Why|Next):)/g, '\n');
  if (!/^(What|Happened|Why|Next):/m.test(normalized)) return null;
  const buckets = { What: [], Happened: [], Why: [], Next: [] };
  let current = null;
  for (const line of normalized.split('\n')) {
    const match = line.match(/^(What|Happened|Why|Next):\s*(.*)$/i);
    if (match) {
      const key = match[1][0].toUpperCase() + match[1].slice(1).toLowerCase();
      current = REASON_KEYS.includes(key) ? key : null;
      if (current && match[2]) buckets[current].push(match[2]);
      continue;
    }
    if (current) buckets[current].push(line);
  }
  if (!buckets.What.length && !buckets.Happened.length) return null;
  return {
    what: buckets.What.join('\n').trim(),
    happened: buckets.Happened.join('\n').trim(),
    why: buckets.Why.join('\n').trim(),
    next: buckets.Next.join('\n').trim(),
  };
}

function issueSeverityClass(type) {
  if (type === 'missing') return styles.issueCardMust;
  if (type === 'question') return styles.issueCardAsk;
  return styles.issueCardHint;
}

function ValidationIssuesPanel({ nodes = [], reason }) {
  if (!nodes.length) return null;
  return (
    <div className={styles.validationPanel}>
      {reason?.what && <p className={styles.validationLead}>{reason.what}</p>}
      <ul className={styles.issueList}>
        {nodes.map((node, index) => {
          const severity =
            node.severityLabel ||
            (node.type === 'missing'
              ? 'Must fix'
              : node.type === 'question'
                ? 'Needs your answer'
                : 'Suggestion');
          const where =
            typeof node.index === 'number'
              ? `Step ${node.index}`
              : node.scope === 'scenario'
                ? 'Whole scenario'
                : 'Step';
          const title = node.title || 'Instruction issue';
          const fix = node.fix_hint || node.fixHint;
          const question = node.question;
          return (
            <li
              key={`${node.rule || title}-${index}`}
              className={`${styles.issueCard} ${issueSeverityClass(node.type)}`}
            >
              <div className={styles.issueCardHeader}>
                <span className={styles.issueBadge}>{severity}</span>
                <span className={styles.issueWhere}>
                  {where} — {title}
                </span>
              </div>
              {node.message && <p className={styles.issueMessage}>{node.message}</p>}
              {fix && (
                <p className={styles.issueFix}>
                  <strong>How to fix:</strong> {fix}
                </p>
              )}
              {question && (
                <p className={styles.issueFix}>
                  <strong>Please clarify:</strong> {question}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {reason?.why && (
        <p className={styles.validationMeta}>
          <strong>Why:</strong> {reason.why}
        </p>
      )}
      {reason?.next && (
        <p className={styles.validationMeta}>
          <strong>Next:</strong> {reason.next}
        </p>
      )}
    </div>
  );
}

function FourPartDetails({ parts }) {
  if (!parts) return null;
  return (
    <div className={styles.reasonPanel}>
      {parts.what && <DetailRow label="What" value={parts.what} />}
      {parts.happened && <DetailRow label="What happened" value={parts.happened} />}
      {parts.why && <DetailRow label="Why" value={parts.why} />}
      {parts.next && <DetailRow label="Next" value={parts.next} />}
    </div>
  );
}

function taskKeyFromTask(task) {
  if (!task) return null;
  if (task.id != null) return `id:${task.id}`;
  if (task.index != null) return `idx:${task.index}`;
  if (task.text) return `text:${task.text}`;
  return null;
}

function taskKey(entry) {
  return taskKeyFromTask(entry?.task);
}

function isOmitBody(text) {
  if (!text) return false;
  return /(reason|reasoning|details) (hidden|omitted)|secrets in use|step uses secret/i.test(String(text));
}

function failureWhyFromChildren(children = []) {
  for (let i = children.length - 1; i >= 0; i -= 1) {
    const child = children[i];
    if (child.status !== 'error' && child.category !== 'recover') continue;
    const why = child.outcome || child.body || child.title;
    if (why && !isOmitBody(why)) return String(why);
    if (why) return String(why);
  }
  return null;
}

function groupTimeline(entries) {
  const groups = [];
  const indexByKey = new Map();

  entries.forEach((entry) => {
    const key = taskKey(entry);
    if (!key) {
      groups.push({ type: 'entry', entry });
      return;
    }
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({
        type: 'task',
        key,
        task: entry.task,
        status: entry.task?.status || entry.status || 'running',
        usesSecret: Boolean(entry.usesSecretTask),
        children: [entry],
      });
      return;
    }
    const group = groups[indexByKey.get(key)];
    group.children.push(entry);
    if (entry.usesSecretTask) group.usesSecret = true;
    if (entry.status === 'error') group.status = 'error';
    else if (entry.status === 'completed' && group.status !== 'error') group.status = 'completed';
    else if (entry.status === 'running' && group.status !== 'error') group.status = 'running';
  });

  return groups;
}

function hasExpandableDetails(entry) {
  return Boolean(
    entry?.body ||
      entry?.pageUrl ||
      entry?.playwrightCode ||
      entry?.screenshotUrl ||
      entry?.outcome ||
      (entry?.validationNodes || []).length ||
      entry?.validationIssue ||
      (entry?.highlightedRefs || []).length ||
      entry?.kind === 'thought'
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function truncateLine(text, max = 160) {
  const value = String(text || '').trim();
  if (!value) return null;
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** One readable line so the story is legible without expanding anything. */
function summaryLine(entry) {
  const nodes = entry.validationNodes || [];
  if (nodes.length) {
    const must = nodes.filter((n) => n.type === 'missing').length;
    const ask = nodes.filter((n) => n.type === 'question').length;
    const sug = nodes.length - must - ask;
    const bits = [];
    if (must) bits.push(`${must} must-fix`);
    if (ask) bits.push(`${ask} need your answer`);
    if (sug) bits.push(`${sug} suggestion${sug === 1 ? '' : 's'}`);
    return bits.join(' · ') || `${nodes.length} issue(s)`;
  }
  if (entry.validationIssue?.message) {
    return truncateLine(entry.validationIssue.message);
  }
  const parts = parseFourPartReason(entry.body || entry.outcome || '');
  if (parts?.what) return truncateLine(parts.what);
  const raw = entry.outcome || entry.body || '';
  const text = String(raw)
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line && !/^(What|Happened|Why|Next):/i.test(line));
  if (!text || text === entry.title) return null;
  return truncateLine(text);
}

function TimelineEntry({ entry, expanded, onToggle, isLatest, showSecretChip = true }) {
  const categoryLabel = CATEGORY_LABEL[entry.category] || entry.tag || entry.kind;
  const canExpand = hasExpandableDetails(entry);
  const isThought = entry.kind === 'thought' || entry.category === 'thought' || entry.category === 'directive';
  const omitBody = isOmitBody(entry.body);
  // The agent explains why it withheld text — keep its wording, just style it as omitted
  const displayBody = entry.body || (omitBody ? OMIT_REASONING_COPY : '');
  const demoteTag = entry.category === 'guardrail' || entry.category === 'system';
  const summary = summaryLine(entry);
  const reasonParts = parseFourPartReason(displayBody);
  const validationNodes = entry.validationNodes || [];
  const singleIssue = entry.validationIssue
    ? [entry.validationIssue]
    : [];

  return (
    <div
      className={`${styles.timelineItem} ${styles.timelineItemCompact} ${isLatest ? styles.timelineItemLatest : ''} ${
        entry.status === 'error' ? styles.timelineItemFailed : ''
      }`}
      data-latest={isLatest ? '1' : undefined}
    >
      <span className={timelineDotClass(entry.status)} aria-hidden />
      <button
        type="button"
        className={styles.timelineRowBtn}
        onClick={() => canExpand && onToggle?.(entry.id)}
        disabled={!canExpand}
        aria-expanded={expanded}
      >
        <div className={styles.timelineHeader}>
          <div className={styles.timelineHeaderMain}>
            <span className={styles.timelineTime}>{entry.time}</span>
            {categoryLabel && !demoteTag && <span className={styles.tag}>{categoryLabel}</span>}
            {entry.tool && entry.tool !== categoryLabel && (
              <span className={styles.tag}>{entry.tool}</span>
            )}
            <span className={styles.timelineTitle}>{entry.title}</span>
            {showSecretChip && entry.usesSecretTask && (
              <span className={styles.secretChip} title={SECRET_MASK_NOTE}>
                Secret
              </span>
            )}
          </div>
          <div className={styles.timelineHeaderAside}>
            {canExpand && (
              <span className={styles.expandHint} aria-hidden>
                {expanded ? (
                  <ChevronDownIcon style={{ width: 12, height: 12 }} />
                ) : (
                  <ChevronRightIcon style={{ width: 12, height: 12 }} />
                )}
              </span>
            )}
            <StatusPill status={entry.status} />
          </div>
        </div>
        {summary && (
          <div
            className={`${styles.entrySummary} ${
              entry.status === 'error' ? styles.entrySummaryError : ''
            }`}
          >
            {summary}
          </div>
        )}
      </button>

      {expanded && canExpand && (
        <div className={styles.timelineDetails}>
          {validationNodes.length > 0 ? (
            <ValidationIssuesPanel nodes={validationNodes} reason={reasonParts} />
          ) : singleIssue.length > 0 ? (
            <ValidationIssuesPanel nodes={singleIssue} />
          ) : reasonParts ? (
            <FourPartDetails parts={reasonParts} />
          ) : (
            (displayBody || omitBody) && (
              <div
                className={`${styles.thoughtBox} ${isThought ? styles.thoughtBoxProse : ''} ${
                  omitBody ? styles.thoughtBoxOmit : ''
                }`}
              >
                <div className={styles.thoughtLabel}>
                  {omitBody ? 'Secret value' : isThought ? 'Why the agent did this' : 'Details'}
                </div>
                <div className={styles.thoughtBody}>{displayBody || OMIT_REASONING_COPY}</div>
              </div>
            )
          )}

          {entry.status === 'error' && entry.outcome && !reasonParts && !validationNodes.length && (
            <DetailRow label="What went wrong" value={entry.outcome} />
          )}
          <DetailRow label="Browser command" value={entry.tool} />
          <DetailRow label="Page address" value={entry.pageUrl} />
          {entry.status !== 'error' && <DetailRow label="Result" value={entry.outcome} />}
          <DetailRow label="Step number" value={entry.stepNumber != null ? String(entry.stepNumber) : null} />
          <DetailRow label="Elements used" value={(entry.highlightedRefs || []).join(', ')} />

          {entry.playwrightCode && (
            <div className={styles.thoughtBox}>
              <div className={styles.thoughtLabel}>Test code for this action</div>
              <pre className={styles.codeBlock}>{entry.playwrightCode}</pre>
            </div>
          )}

          {entry.screenshotUrl && (
            <div className={styles.shotPreview}>
              <div className={styles.thoughtLabel}>What the page looked like</div>
              <img src={entry.screenshotUrl} alt={entry.title || 'Step screenshot'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskGroup({
  item,
  expandedIds,
  onToggleEntry,
  groupOpen,
  onToggleGroup,
  latestEntryId,
  secretTaskKeys = new Set(),
}) {
  const task = item.task || {};
  const title =
    task.index != null && task.total != null
      ? `Step ${task.index}/${task.total}`
      : task.index != null
        ? `Step ${task.index}`
        : 'Instruction';
  const keyForSecret = taskKeyFromTask(task);
  const isSecret =
    item.usesSecret || (keyForSecret && secretTaskKeys.has(keyForSecret));
  const subtitle = isSecret
    ? task.text && !/\$\{_id:|password\s*=/i.test(task.text)
      ? task.text
      : GENERIC_SECRET_TASK
    : task.text || 'Waiting for the step description…';
  const failed = item.status === 'error';
  const why = failed ? failureWhyFromChildren(item.children) : null;

  return (
    <div
      className={`${styles.timelineItem} ${styles.taskGroup} ${failed ? styles.taskGroupFailed : ''}`}
      data-failed={failed ? '1' : undefined}
    >
      <span className={timelineDotClass(item.status)} aria-hidden />
      <button
        type="button"
        className={styles.timelineRowBtn}
        onClick={() => onToggleGroup(item.key)}
        aria-expanded={groupOpen}
      >
        <div className={styles.timelineHeader}>
          <div className={styles.timelineHeaderMain}>
            <span className={styles.expandHint} aria-hidden>
              {groupOpen ? (
                <ChevronDownIcon style={{ width: 12, height: 12 }} />
              ) : (
                <ChevronRightIcon style={{ width: 12, height: 12 }} />
              )}
            </span>
            <span className={styles.timelineTitle}>
              {title}
              <span className={styles.taskSubtitle}> · {subtitle}</span>
            </span>
            {isSecret && (
              <span className={styles.secretChip} title={SECRET_MASK_NOTE}>
                Secret
              </span>
            )}
            <span className={styles.childCount} title={`${item.children.length} events in this step`}>
              {item.children.length}
            </span>
          </div>
          <StatusPill status={item.status} />
        </div>
        {why && (
          <div className={styles.groupWhy} title={why}>
            What went wrong: {why}
          </div>
        )}
      </button>

      {groupOpen && (
        <div className={styles.taskChildren}>
          {item.children.map((child) => (
            <TimelineEntry
              key={child.id}
              entry={child}
              expanded={expandedIds.has(child.id)}
              onToggle={onToggleEntry}
              isLatest={child.id === latestEntryId}
              showSecretChip={!isSecret}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AgentMindTimeline({
  timeline = [],
  stats,
  filter = 'story',
  onFilterChange,
  isRunning = false,
  variablesUsedRows = [],
  rawTimelineCount = null,
}) {
  const sourceEmpty = (rawTimelineCount ?? timeline.length) === 0;
  const filterEmpty = !sourceEmpty && timeline.length === 0;
  const grouped = useMemo(() => groupTimeline(timeline), [timeline]);
  const latestEntryId = timeline.length ? timeline[timeline.length - 1].id : null;

  const secretTaskKeys = useMemo(() => {
    const keys = new Set();
    for (const entry of timeline) {
      if (entry?.usesSecretTask) {
        const k = taskKey(entry);
        if (k) keys.add(k);
      }
    }
    return keys;
  }, [timeline]);

  const latestFailed = useMemo(() => {
    const failed = grouped.filter((g) => g.type === 'task' && g.status === 'error');
    return failed.length ? failed[failed.length - 1] : null;
  }, [grouped]);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [followLive, setFollowLive] = useState(true);

  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const prevLenRef = useRef(0);
  const lastAutoGroupKeyRef = useRef(null);

  const toggleEntry = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (prev.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
    setCollapsedGroups(new Set(grouped.filter((g) => g.type === 'task').map((g) => g.key)));
  };

  const expandAll = () => {
    setCollapsedGroups(new Set());
    const next = new Set();
    for (const g of grouped) {
      if (g.type === 'entry' && hasExpandableDetails(g.entry)) next.add(g.entry.id);
      if (g.type === 'task') {
        for (const child of g.children) {
          if (hasExpandableDetails(child)) next.add(child.id);
        }
      }
    }
    setExpandedIds(next);
  };

  const jumpToLatestFailure = () => {
    setFollowLive(false);
    stickToBottomRef.current = false;
    if (latestFailed) {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        next.delete(latestFailed.key);
        return next;
      });
      setPinnedGroupKeys((pins) => new Set(pins).add(latestFailed.key));
      const errIds = latestFailed.children
        .filter(
          (c) =>
            c.status === 'error' ||
            c.category === 'recover' ||
            (c.category === 'evaluation' && c.status === 'error')
        )
        .filter(hasExpandableDetails)
        .map((c) => c.id);
      if (errIds.length) {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          errIds.forEach((id) => next.add(id));
          return next;
        });
      }
    }
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      const fails = el.querySelectorAll('[data-failed="1"]');
      const target = fails.length ? fails[fails.length - 1] : null;
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  // Auto-expand ALL error children in failed groups (not only latest timeline id)
  useEffect(() => {
    const errorIds = [];
    for (const g of grouped) {
      if (g.type !== 'task' || g.status !== 'error') continue;
      for (const child of g.children) {
        const shouldOpen =
          child.status === 'error' ||
          child.category === 'recover' ||
          (child.category === 'evaluation' && child.status === 'error');
        if (shouldOpen && hasExpandableDetails(child)) errorIds.push(child.id);
      }
    }
    if (!errorIds.length) return;
    setExpandedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of errorIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [grouped]);

  // Failed groups: force open when they appear
  useEffect(() => {
    const failed = grouped.filter((g) => g.type === 'task' && g.status === 'error').map((g) => g.key);
    if (!failed.length) return;
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      failed.forEach((k) => next.delete(k));
      return next;
    });
  }, [grouped]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 48;
    stickToBottomRef.current = atBottom;
    setFollowLive(atBottom);
  };

  useEffect(() => {
    const grew = timeline.length > prevLenRef.current;
    prevLenRef.current = timeline.length;
    if (!grew) return;
    if (!followLive && !stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const fails = el.querySelectorAll('[data-failed="1"]');
      const failNode = fails.length ? fails[fails.length - 1] : null;
      const latestNode = el.querySelector('[data-latest="1"]');
      const target = failNode || latestNode;
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, [timeline.length, followLive, latestEntryId]);

  const emptyCopy = (() => {
    if (sourceEmpty && !isRunning) {
      return (
        <>
          Press <strong>Run</strong> to follow each step here: what the agent planned, what it did
          on the page, and whether it worked.
        </>
      );
    }
    if (sourceEmpty && isRunning) {
      return <>Connecting to the agent — the first step will appear here in a moment.</>;
    }
    if (filterEmpty) {
      return (
        <>
          Nothing to show in this view yet.{' '}
          <button type="button" className={styles.inlineLink} onClick={() => onFilterChange?.('story')}>
            Back to Story
          </button>
          {' · '}
          <button type="button" className={styles.inlineLink} onClick={() => onFilterChange?.('all')}>
            Show every event
          </button>
        </>
      );
    }
    return null;
  })();

  const failedStepLabel = latestFailed
    ? latestFailed.task?.index != null
      ? `Step ${latestFailed.task.index} failed — see what went wrong`
      : 'A step failed — see what went wrong'
    : null;

  return (
    <section className={styles.panel} aria-label="Agent Mind Timeline">
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Agent Mind</span>
        <div className={styles.mindHeaderControls}>
          <div className={styles.linkRow} role="group" aria-label="Mind filters">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.linkBtn} ${filter === item.id ? styles.linkBtnActive : ''}`}
                onClick={() => onFilterChange?.(item.id)}
                aria-pressed={filter === item.id}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              className={`${styles.linkBtn} ${filter === 'all' ? styles.linkBtnActive : ''}`}
              onClick={() => onFilterChange?.('all')}
              aria-pressed={filter === 'all'}
              title="Every event the agent sent, including technical detail"
            >
              All
            </button>
          </div>
          <div className={styles.linkRow} role="group" aria-label="Mind controls">
            <button
              type="button"
              className={styles.linkBtn}
              onClick={expandAll}
              title="Show the full detail of every step"
            >
              Expand
            </button>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={collapseAll}
              title="Show step headlines only"
            >
              Collapse
            </button>
            <button
              type="button"
              className={`${styles.linkBtn} ${followLive ? styles.linkBtnActive : ''}`}
              onClick={() => {
                setFollowLive(true);
                stickToBottomRef.current = true;
                const el = scrollRef.current;
                if (el) el.scrollTop = el.scrollHeight;
              }}
              aria-pressed={followLive}
              title="Keep the newest step in view"
            >
              {followLive ? '● Live' : 'Follow'}
            </button>
          </div>
        </div>
      </div>

      {failedStepLabel && (
        <button
          type="button"
          className={styles.failureChip}
          onClick={jumpToLatestFailure}
          title="Go to the step that failed"
        >
          {failedStepLabel}
        </button>
      )}

      <div className={styles.panelBody} ref={scrollRef} onScroll={onScroll}>
        <div className={`${styles.timeline} ${styles.timelineCompact}`}>
          {emptyCopy && <p className={styles.thoughtBody}>{emptyCopy}</p>}

          {grouped.map((item) => {
            if (item.type === 'entry') {
              return (
                <TimelineEntry
                  key={item.entry.id}
                  entry={item.entry}
                  expanded={expandedIds.has(item.entry.id)}
                  onToggle={toggleEntry}
                  isLatest={item.entry.id === latestEntryId}
                />
              );
            }
            return (
              <TaskGroup
                key={item.key}
                item={item}
                expandedIds={expandedIds}
                onToggleEntry={toggleEntry}
                groupOpen={!collapsedGroups.has(item.key)}
                onToggleGroup={toggleGroup}
                latestEntryId={latestEntryId}
                secretTaskKeys={secretTaskKeys}
              />
            );
          })}
        </div>
      </div>

      <div className={`${styles.panelFooter} ${styles.statsBar}`}>
        <span>
          Steps{' '}
          <strong>
            {stats?.stepsCurrent || 0}/{stats?.stepsTotal || 0}
          </strong>
        </span>
        <span>
          Actions <strong>{stats?.actionsCurrent || 0}</strong>
        </span>
        {(stats?.retries || 0) > 0 && (
          <span>
            Retries <strong>{stats.retries}</strong>
          </span>
        )}
        <span>
          Vars <strong>{variablesUsedRows.length || stats?.variablesUsed || 0}</strong>
        </span>
      </div>
      {variablesUsedRows.length > 0 && (
        <div className={styles.varsPanel} aria-label="Variables used">
          {variablesUsedRows.map((row) => (
            <div key={row.key || row.name} className={styles.varsRow}>
              <span className={styles.varsKey}>
                {row.name || row.key}
                {row.isSecret ? <span className={styles.secretChip}>Secret</span> : null}
              </span>
              <span className={styles.varsValue}>
                {row.isSecret ? SECRET_MASK : String(row.value ?? '')}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
